import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { naira, StatusPill } from '@/components/affiliate/AffiliateWidgets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InviteForm = ({ headers, onDone }) => {
  const [form, setForm] = useState({ email: '', full_name: '', commission_rate: '', code: '' });
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, commission_rate: form.commission_rate === '' ? null : Number(form.commission_rate), code: form.code || null };
      const { data } = await axios.post(`${API}/admin/affiliates`, body, { headers });
      setResult(data);
      setForm({ email: '', full_name: '', commission_rate: '', code: '' });
      toast.success('Affiliate invited');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invite failed');
    }
  };
  return (
    <form onSubmit={submit} className="grid md:grid-cols-5 gap-3 items-end" data-testid="affiliate-invite-form">
      {[['full_name', 'Full name'], ['email', 'Email'], ['commission_rate', 'Commission % (blank = default)'], ['code', 'Code (optional)']].map(([k, l]) => (
        <div key={k}><Label className="text-xs">{l}</Label><Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required={k === 'email' || k === 'full_name'} data-testid={`invite-${k}`} /></div>
      ))}
      <Button type="submit" data-testid="invite-submit">Invite</Button>
      {result && (
        <div className="md:col-span-5 text-sm bg-green-50 border border-green-200 rounded p-3" data-testid="invite-result">
          Invited <b>{result.email}</b> · code <b>{result.code}</b> · link {result.referral_link}
          {result.temp_password && <> · temporary password <b>{result.temp_password}</b> (also emailed)</>}
        </div>
      )}
    </form>
  );
};

const AffiliateRow = ({ a, headers, onDone }) => {
  const [rate, setRate] = useState(a.commission_rate);
  const save = async (changes) => {
    try {
      await axios.put(`${API}/admin/affiliates/${a.id}`, changes, { headers });
      toast.success('Affiliate updated');
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };
  return (
    <tr className="border-b" data-testid={`affiliate-row-${a.code}`}>
      <td className="py-2 pr-3"><p className="font-semibold">{a.full_name}</p><p className="text-xs text-gray-500">{a.email}</p></td>
      <td className="pr-3 font-mono">{a.code}</td>
      <td className="pr-3"><div className="flex gap-1 items-center"><Input className="w-20 h-8" type="number" value={rate} onChange={(e) => setRate(e.target.value)} data-testid={`rate-input-${a.code}`} /><Button size="sm" variant="outline" onClick={() => save({ commission_rate: Number(rate) })} data-testid={`rate-save-${a.code}`}>Save</Button></div></td>
      <td className="pr-3">{a.stats.orders_count}</td>
      <td className="pr-3">{naira(a.stats.sales_total)}</td>
      <td className="pr-3">{naira(a.stats.pending_commission + a.stats.awaiting_approval_commission)}</td>
      <td className="pr-3">{naira(a.stats.approved_commission)}</td>
      <td className="pr-3">{naira(a.stats.paid_out)}</td>
      <td className="pr-3 font-semibold">{naira(a.stats.available_balance)}</td>
      <td><Switch checked={a.status === 'active'} onCheckedChange={(v) => save({ status: v ? 'active' : 'suspended' })} data-testid={`status-toggle-${a.code}`} /></td>
    </tr>
  );
};

const Commissions = ({ headers, list, onDone }) => {
  const [selected, setSelected] = useState([]);
  const act = async (kind) => {
    const reason = kind === 'reject' ? window.prompt('Reason for rejecting?') : null;
    if (kind === 'reject' && reason === null) return;
    try {
      const { data } = await axios.post(`${API}/admin/commissions/${kind}`, { ids: selected, reason }, { headers });
      toast.success(`${data.approved ?? data.rejected} commission(s) ${kind === 'approve' ? 'approved' : 'rejected'}`);
      setSelected([]);
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div>
      <div className="flex gap-2 mb-3">
        <Button size="sm" disabled={!selected.length} onClick={() => act('approve')} data-testid="commissions-approve">Approve selected</Button>
        <Button size="sm" variant="outline" disabled={!selected.length} onClick={() => act('reject')} data-testid="commissions-reject">Reject selected</Button>
      </div>
      <div className="overflow-x-auto max-h-96">
        <table className="w-full text-sm" data-testid="commissions-table">
          <thead><tr className="text-left text-gray-500 border-b"><th></th><th>Date</th><th>Affiliate</th><th>Order</th><th>Order value</th><th>Commission</th><th>State</th><th>Order status</th><th>Status</th></tr></thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-b">
                <td><input type="checkbox" disabled={!['pending', 'eligible', 'approved'].includes(c.status)} checked={selected.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`commission-check-${c.id}`} /></td>
                <td className="py-2">{new Date(c.created_at).toLocaleDateString()}</td><td>{c.affiliate_code}</td><td>#{c.order_id.slice(0, 8)}</td>
                <td>{naira(c.base_amount)}</td><td>{naira(c.amount)}</td><td>{c.delivery_state || '-'}</td><td>{c.order_state}</td><td><StatusPill status={c.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && <p className="text-sm text-gray-500 py-4 text-center">No commissions yet</p>}
      </div>
    </div>
  );
};

const ProgrammeSettings = ({ headers }) => {
  const [s, setS] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/affiliate-settings`, { headers }).then((r) => setS(r.data)); }, [headers]);
  if (!s) return null;
  const save = async () => {
    try {
      await axios.put(`${API}/admin/affiliate-settings`, { default_commission_rate: Number(s.default_commission_rate), cookie_days: Number(s.cookie_days), require_commission_approval: s.require_commission_approval }, { headers });
      toast.success('Programme settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };
  return (
    <div className="grid md:grid-cols-4 gap-4 items-end" data-testid="affiliate-settings">
      <div><Label className="text-xs">Default commission %</Label><Input type="number" value={s.default_commission_rate} onChange={(e) => setS({ ...s, default_commission_rate: e.target.value })} data-testid="aff-default-rate" /></div>
      <div><Label className="text-xs">Referral link lasts (days)</Label><Input type="number" value={s.cookie_days} onChange={(e) => setS({ ...s, cookie_days: e.target.value })} data-testid="aff-cookie-days" /></div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={s.require_commission_approval} onCheckedChange={(v) => setS({ ...s, require_commission_approval: v })} data-testid="aff-require-approval" />Admin must approve commissions after delivery</label>
      <Button onClick={save} data-testid="aff-settings-save">Save settings</Button>
    </div>
  );
};

export default function AffiliatesManagement() {
  const { token } = useAuth();
  const [headers] = useState({ Authorization: `Bearer ${token}` });
  const [affiliates, setAffiliates] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    const [a, c, r] = await Promise.all([
      axios.get(`${API}/admin/affiliates`, { headers }), axios.get(`${API}/admin/commissions`, { headers }), axios.get(`${API}/admin/affiliate-reports`, { headers })]);
    setAffiliates(a.data); setCommissions(c.data); setReport(r.data);
  }, [headers]);
  useEffect(() => { load().catch(() => toast.error('Failed to load affiliates')); }, [load]);

  return (
    <div className="space-y-6" data-testid="affiliates-management">
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[['Clicks', report.totals.clicks], ['Pending', naira(report.totals.pending)], ['Awaiting approval', naira(report.totals.eligible)], ['Approved', naira(report.totals.approved)], ['Paid out', naira(report.totals.paid_out)], ['Reversed', naira(report.totals.reversed)]].map(([l, v]) => (
            <Card key={l}><CardContent className="pt-4"><p className="text-xs text-gray-500">{l}</p><p className="text-xl font-bold" data-testid={`aff-report-${l.toLowerCase().replace(/\s/g, '-')}`}>{v}</p></CardContent></Card>
          ))}
        </div>
      )}
      <Card><CardHeader><CardTitle>Invite an affiliate / brand ambassador</CardTitle></CardHeader><CardContent><InviteForm headers={headers} onDone={load} /></CardContent></Card>
      <Card><CardHeader><CardTitle>Programme settings</CardTitle></CardHeader><CardContent><ProgrammeSettings headers={headers} /></CardContent></Card>
      <Card>
        <CardHeader><CardTitle>Affiliates ({affiliates.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="affiliates-table">
            <thead><tr className="text-left text-gray-500 border-b"><th>Affiliate</th><th>Code</th><th>Rate %</th><th>Orders</th><th>Sales</th><th>Pending</th><th>Earned</th><th>Paid</th><th>Balance</th><th>Active</th></tr></thead>
            <tbody>{affiliates.map((a) => <AffiliateRow key={a.id} a={a} headers={headers} onDone={load} />)}</tbody>
          </table>
        </CardContent>
      </Card>
      <Card><CardHeader><CardTitle>Commissions</CardTitle></CardHeader><CardContent><Commissions headers={headers} list={commissions} onDone={load} /></CardContent></Card>
      {report && (
        <div className="grid md:grid-cols-3 gap-6">
          {[['Top affiliates', report.top_affiliates, ['code', 'orders', 'sales']], ['Sales by delivery state', report.states, ['state', 'orders', 'sales']], ['Monthly performance', report.monthly, ['month', 'orders', 'commission']]].map(([title, rows, cols]) => (
            <Card key={title}><CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader><CardContent>
              <table className="w-full text-sm"><tbody>{rows.map((r, i) => <tr key={i} className="border-b">{cols.map((c) => <td key={c} className="py-1">{['sales', 'commission'].includes(c) ? naira(r[c]) : r[c]}</td>)}</tr>)}</tbody></table>
              {!rows.length && <p className="text-sm text-gray-500">No data yet</p>}
            </CardContent></Card>
          ))}
        </div>
      )}
    </div>
  );
}
